import { useState } from "react";
import { toast } from "sonner";
import { Trash2Icon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { TableCell, TableRow } from "@/components/ui/table";
import { Etiqueta } from "@/components/ui/etiqueta";
import { useAceptarPropuesta, useEliminarLinea, useFijarPrecio } from "@/hooks/useValorizacion";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import type { Moneda } from "@/lib/informes";
import {
  ETIQUETA_ESTADO_DE_LINEA,
  TONO_DE_ESTADO_DE_LINEA,
  cuerpoDelPrecio,
  estadoDeLaLinea,
  exigeMotivo,
  formatearCantidad,
  formatearPrecio,
  interpretarPrecio,
  textoDeNumero,
  textoOGuion,
  type LineaValorizada,
} from "@/lib/valorizacion";

const MONEDAS: Moneda[] = ["UF", "CLP", "USD"];

interface LineaDeValorizacionProps {
  informeId: number;
  linea: LineaValorizada;
}

/**
 * Una línea del rubro: lo que hay (cantidad), lo que el sistema propone, el
 * precio que fija la persona y lo que eso suma.
 *
 * El estado vive en la FILA y no en la tabla: el padre la monta con una `key`
 * que cambia al guardar, así que los campos vuelven a leer lo guardado sin un
 * efecto que copie props a estado.
 */
export function LineaDeValorizacion({ informeId, linea }: LineaDeValorizacionProps) {
  const fijar = useFijarPrecio(informeId);
  const aceptar = useAceptarPropuesta(informeId);
  const eliminar = useEliminarLinea(informeId);

  const textoInicial = linea.precio ? textoDeNumero(linea.precio.valor) : "";
  const monedaInicial: Moneda = linea.precio?.moneda ?? linea.propuesta?.moneda ?? "UF";
  const motivoInicial = linea.precio?.motivo ?? "";
  const incluidoInicial = linea.incluido_en ?? "";

  const [texto, setTexto] = useState(textoInicial);
  const [moneda, setMoneda] = useState<Moneda>(monedaInicial);
  const [motivo, setMotivo] = useState(motivoInicial);
  const [incluidoEn, setIncluidoEn] = useState(incluidoInicial);
  const [verIncluido, setVerIncluido] = useState(incluidoInicial !== "");
  const [error, setError] = useState<string | null>(null);

  const lectura = interpretarPrecio(texto, moneda);
  const estado = estadoDeLaLinea(linea);
  const pideMotivo = lectura.estado === "ok" && exigeMotivo(linea, lectura.valor, moneda);
  const cambio =
    texto.trim() !== textoInicial ||
    moneda !== monedaInicial ||
    (pideMotivo && motivo.trim() !== motivoInicial) ||
    incluidoEn.trim() !== incluidoInicial;
  const ocupada = fijar.isPending || aceptar.isPending || eliminar.isPending;
  const puedeQuitarse = linea.manual || linea.fuente_eliminada;
  const cantidad = formatearCantidad(linea.cantidad, linea.unidad);

  function guardar() {
    const preparado = cuerpoDelPrecio(linea, { texto, moneda, motivo, incluidoEn });

    if (!preparado.ok) {
      setError(preparado.error);
      return;
    }

    setError(null);
    fijar.mutate(
      { lineaId: linea.id, cuerpo: preparado.cuerpo },
      {
        onError: (falla) => {
          const campos = parseValidationErrors(falla);
          setError(
            campos?.motivo ?? campos?.precio_unitario ?? getApiErrorMessage(falla, "No pudimos guardar el valor. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <TableRow className="align-top">
      <TableCell className="min-w-44 whitespace-normal">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={linea.fuente_eliminada ? "text-ink-muted font-bold line-through" : "text-ink font-bold"}>
            {linea.descripcion}
          </span>
          {linea.manual ? <Badge variant="outline">Manual</Badge> : null}
        </div>
        <div className="text-ink-body mt-0.5 font-mono">
          {cantidad ?? <span className="text-warning-texto font-sans font-bold">Falta la cantidad</span>}
        </div>
        {linea.cantidad_cambio ? (
          <p className="text-warning-texto bg-warning-soft mt-1.5 rounded-md px-2 py-1">
            La cantidad cambió: era {formatearCantidad(linea.cantidad_cambio.anterior, linea.unidad) ?? "sin cantidad"} y
            ahora es {cantidad ?? "desconocida"}. Revisa el precio.
          </p>
        ) : null}
        {linea.fuente_eliminada ? (
          <p className="text-warning-texto bg-warning-soft mt-1.5 rounded-md px-2 py-1">
            Esta línea ya no está en el expediente y no suma al valor del rubro. Quítala para completar la valorización.
          </p>
        ) : null}
        {linea.incluido_en ? (
          <p className="text-info-soft-foreground mt-1.5 font-bold">Incluida en el valor de {linea.incluido_en}</p>
        ) : null}
        {puedeQuitarse ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-danger-texto mt-1.5 -ml-2"
            aria-label={`Quitar la línea ${linea.descripcion}`}
            disabled={ocupada}
            onClick={() =>
              eliminar.mutate(linea.id, {
                onError: (falla) => toast.error(getApiErrorMessage(falla, "No pudimos quitar la línea.")),
              })
            }
          >
            <Trash2Icon aria-hidden /> Quitar la línea
          </Button>
        ) : null}
      </TableCell>

      <TableCell className="min-w-40 whitespace-normal">
        {linea.propuesta ? (
          <>
            <div className="text-ink font-mono font-bold">
              {linea.propuesta.moneda ? formatearPrecio(linea.propuesta.valor, linea.propuesta.moneda) : linea.propuesta.valor}
            </div>
            {linea.propuesta.origen ? <div className="text-ink-muted mt-0.5">{linea.propuesta.origen}</div> : null}
            {linea.propuesta.desviacion_pct !== null ? (
              <div className="text-ink-medio mt-0.5">
                El precio se aparta {Math.abs(linea.propuesta.desviacion_pct).toLocaleString("es-CL")} %
                {linea.propuesta.desviacion_pct >= 0 ? " por encima" : " por debajo"}
              </div>
            ) : null}
          </>
        ) : (
          <span className="text-ink-hint">Sin propuesta</span>
        )}
      </TableCell>

      <TableCell className="min-w-64 whitespace-normal">
        <div className="flex items-start gap-2">
          <Input
            type="text"
            inputMode="decimal"
            aria-label={`Valor final de ${linea.descripcion}`}
            aria-invalid={lectura.estado === "invalido"}
            placeholder="Ingresar"
            value={texto}
            onChange={(evento) => {
              setTexto(evento.target.value);
              setError(null);
            }}
            className="min-w-0 flex-1 text-right font-mono font-bold"
          />
          {/* El wrapper de NativeSelect es w-full: el ancho va en un padre, o aplasta al campo del valor. */}
          <div className="w-24 shrink-0">
            <NativeSelect
              aria-label={`Moneda de ${linea.descripcion}`}
              value={moneda}
              onChange={(evento) => setMoneda(evento.target.value as Moneda)}
            >
              {MONEDAS.map((m) => (
                <NativeSelectOption key={m} value={m}>
                  {m}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        </div>

        {lectura.estado === "ok" && cambio ? <p className="text-ink-muted mt-1 font-mono">{lectura.lectura}</p> : null}
        {lectura.estado === "invalido" ? (
          <p role="alert" className="text-danger-texto mt-1">
            {lectura.mensaje}
          </p>
        ) : null}

        {pideMotivo ? (
          <div className="mt-2 flex items-center gap-2">
            <label htmlFor={`motivo-${linea.id}`} className="text-info-soft-foreground shrink-0 text-xs font-bold">
              Motivo de la corrección
            </label>
            <Input
              id={`motivo-${linea.id}`}
              aria-label={`Motivo de la corrección de ${linea.descripcion}`}
              placeholder="Ej.: se excluye la franja de servidumbre del canal"
              value={motivo}
              onChange={(evento) => {
                setMotivo(evento.target.value);
                setError(null);
              }}
              className="h-10 min-w-0 flex-1 text-[0.8125rem]"
            />
          </div>
        ) : null}

        {verIncluido ? (
          <div className="mt-2">
            <Input
              type="text"
              aria-label={`Incluida en el valor de, para ${linea.descripcion}`}
              placeholder="Incluida en el valor de… (se imprime, no suma)"
              value={incluidoEn}
              onChange={(evento) => setIncluidoEn(evento.target.value)}
            />
          </div>
        ) : (
          <button
            type="button"
            className="text-ink-muted hover:text-ink mt-1.5 font-bold underline-offset-2 hover:underline"
            onClick={() => setVerIncluido(true)}
          >
            Está incluida en el valor de otra línea
          </button>
        )}

        {error ? (
          <p role="alert" className="text-danger-texto mt-1.5 font-bold">
            {error}
          </p>
        ) : null}
      </TableCell>

      <TableCell className="min-w-28 whitespace-normal">
        <Etiqueta tono={TONO_DE_ESTADO_DE_LINEA[estado]}>{ETIQUETA_ESTADO_DE_LINEA[estado]}</Etiqueta>
        {linea.precio?.fijado_en ? (
          <p className="text-ink-hint mt-1">
            Fijado por {linea.precio.fijado_por?.nombre ?? "el sistema"} · {formatearFechaYHora(linea.precio.fijado_en)}
          </p>
        ) : null}
        {linea.precio?.motivo ? <p className="text-info-soft-foreground mt-1">Motivo: {linea.precio.motivo}</p> : null}
      </TableCell>

      <TableCell className="min-w-24 whitespace-normal text-right">
        {linea.estado === "incluida" ? (
          <span className="text-ink-muted font-bold">No suma</span>
        ) : (
          <>
            <div className="text-ink font-mono font-bold">{textoOGuion(linea.total_formateado.uf)}</div>
            {linea.total_formateado.clp ? <div className="text-ink-body font-mono">{linea.total_formateado.clp}</div> : null}
            {linea.total_formateado.usd ? <div className="text-ink-body font-mono">{linea.total_formateado.usd}</div> : null}
          </>
        )}
      </TableCell>

      <TableCell className="min-w-40 whitespace-normal">
        <div className="flex flex-col items-stretch gap-1.5">
          {linea.propuesta && estado !== "aceptado" ? (
            <Button
              type="button"
              size="sm"
              variant="fuerte"
              className="h-auto rounded-[0.625rem] px-3 py-1.5"
              aria-label={`Aceptar propuesta de ${linea.descripcion}`}
              disabled={ocupada}
              onClick={() =>
                aceptar.mutate(linea.id, {
                  onError: (falla) => toast.error(getApiErrorMessage(falla, "No pudimos aceptar la propuesta.")),
                })
              }
            >
              Aceptar propuesta
            </Button>
          ) : null}
          {cambio ? (
            <Button
              type="button"
              size="sm"
              variant="fuerte"
              className="h-auto rounded-[0.625rem] px-3 py-1.5"
              aria-label={`Guardar valor de ${linea.descripcion}`}
              disabled={ocupada}
              onClick={guardar}
            >
              {fijar.isPending ? "Guardando…" : "Guardar valor"}
            </Button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}
